<template>
  <div v-if="form" id="app">
    <Head>
      <Title v-if="form.site">
        {{ form.site }}
      </Title>
    </Head>
    <Transition appear>
      <div key="app" class="h-screen">
        <NuxtLayout>
          <NuxtPage />
        </NuxtLayout>
      </div>
    </Transition>
  </div>
  <div v-else class="m-3 p-3">
    {{ formNotFoundTxt }}
    <!-- <div v-if="form && !form.whitelist.includes(baseUrl)">
      Accès non autorisé !<br>
      <div v-if="['dev', 'rec'].includes(useRuntimeConfig().public.appVersion)">
        Liste des urls autorisées :
        <ul>
          <li v-for="(url,id) in form.whitelist" :key="id">
            {{ url }}
          </li>
        </ul>
      </div>
    </div> -->
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useStore } from "@/stores/store";
import { getStoredResponse } from "@/composables/responseStorage";

const store = useStore();
const route = useRoute();

let gtmIsSet = false;

const { form } = storeToRefs(store);
let formIsLoading = true;

const formNotFoundTxt = ref("");

setTimeout(() => {
  if (formIsLoading) {
    formNotFoundTxt.value = "Form is loading...";
  }
}, 1000);

store
  .getJsonForm()
  .then(() => {
    formIsLoading = false;
    if (!store.form) {
      formNotFoundTxt.value = "Error: Can't fetch form";
    } else {
      formNotFoundTxt.value = "";
    }
  })
  .catch(() => {
    formIsLoading = false;
    formNotFoundTxt.value = "Error: Can't fetch form";
  });

let browserButtonClicked = false;

// Handles Browser Back/Forward Button
window.onpopstate = function (event) {
  route.meta.layout = form.value.layout;
  browserButtonClicked = true;
  if (
    event.state.stepNb !== undefined &&
    event.state.sectionNb !== undefined &&
    event.state.page !== undefined
  ) {
    store.stepNb = event.state.stepNb;
    store.sectionNb = event.state.sectionNb;
    store.page = event.state.page;
  } else {
    store.stepNb = 0;
    store.sectionNb = 0;
    store.page = store.noIntro ? "credit-form" : "";
  }
  setTimeout(() => {
    browserButtonClicked = false;
  }, 500);
  localStorage.setItem("stepNb", store.stepNb.toString());
  localStorage.setItem("sectionNb", store.sectionNb.toString());
};
const page = computed(() => {
  return store.page;
});
const stepNb = computed(() => {
  return store.stepNb;
});
const sectionNb = computed(() => {
  return store.sectionNb;
});

watch([page, stepNb, sectionNb], ([page, stepNb, sectionNb]) => {
  if (browserButtonClicked === false) {
    window.history.pushState({ stepNb, sectionNb, page }, "", "");
  }
});

watch(form, (form) => {
  if (form) {
    // Load custom CSS vars from backend
    if (typeof window !== "undefined") {
      Object.entries(form.colors).forEach((color) => {
        window.document.documentElement.style.setProperty(
          "--" + color[0],
          color[1] as string
        );
      });
      Object.entries(form.fonts).forEach((font) => {
        window.document.documentElement.style.setProperty(
          "--" + font[0],
          font[1] as string
        );
      });
    }
    // Load layout from backend and step/section from localStorage
    if (localStorage.getItem("stepNb") && form) {
      route.meta.layout = form.layout;
      if (
        parseInt(localStorage.getItem("stepNb") as string) >
        form.steps.length - 1
      ) {
        store.stepNb = form.steps.length - 1;
        localStorage.setItem("stepNb", store.stepNb.toString());
      } else {
        store.stepNb = parseInt(localStorage.getItem("stepNb") as string);
      }
      store.page = "credit-form";
    }

    if (localStorage.getItem("sectionNb") && form) {
      route.meta.layout = form.layout;
      if (
        parseInt(localStorage.getItem("sectionNb") as string) >
        form.steps[store.stepNb].sections.length - 1
      ) {
        store.sectionNb = form.steps[store.stepNb].sections.length - 1;
        localStorage.setItem("sectionNb", store.sectionNb.toString());
      } else {
        store.sectionNb = parseInt(localStorage.getItem("sectionNb") as string);
      }
      store.page = "credit-form";
    }
    if (localStorage.getItem("noIntro") && form) {
      route.meta.layout = form.layout;
      store.page = "credit-form";
    }

    if (localStorage.getItem("xtor") && form) {
      store.xtor = localStorage.getItem("xtor");
    }

    if (localStorage.getItem("code_aff") && form) {
      store.code_aff = localStorage.getItem("code_aff");
    }

    const todayDate = new Date();
    if (localStorage.getItem("saveTimestamp")) {
      const timeDiff =
        todayDate.getTime() - localStorage.getItem("saveTimestamp");
      const isOlderThan24Hours = timeDiff > 24 * 60 * 60 * 1000;
      if (isOlderThan24Hours) {
        localStorage.setItem("sectionNb", "0");
        store.sectionNb = 0;
        localStorage.setItem("stepNb", "0");
        store.stepNb = 0;
        localStorage.removeItem("response");
        store.response = {};
        localStorage.setItem("saveTimestamp", todayDate.getTime());
      }
    } else {
      localStorage.setItem("saveTimestamp", todayDate.getTime());
      localStorage.setItem("sectionNb", "0");
      store.sectionNb = 0;
      localStorage.setItem("stepNb", "0");
      store.stepNb = 0;
      localStorage.removeItem("response");
      store.response = {};
    }

    const storedResponse = getStoredResponse();
    const hasStoredResponse = Object.keys(storedResponse).length > 0;
    Object.values(form.steps).forEach((step) => {
      step.sections.forEach((section) => {
        section.component_groups.forEach((componentGroup) => {
          componentGroup.components.forEach((cpt) => {
            if (
              ((route.query.xtor !== "" && route.query.xtor !== undefined) ||
                localStorage.getItem("xtor")) &&
              cpt.name === "xtor" &&
              (!hasStoredResponse || !storedResponse.xtor)
            ) {
              cpt.value = localStorage.getItem("xtor")
                ? (localStorage.getItem("xtor") as string)
                : (route.query.xtor as string);
            }
            if (
              ((route.query.codeAff !== "" && route.query.codeAff !== undefined) ||
                localStorage.getItem("code_aff")) &&
              cpt.name === "code_aff" &&
              (!hasStoredResponse || !storedResponse.code_aff)
            ) {
              cpt.value = localStorage.getItem("code_aff")
                ? (localStorage.getItem("code_aff") as string)
                : (route.query.codeAff as string);
            }
          });
        });
      });
    }, {});

    // Google analytics
    if (form.gtm_id && form.gtm_id.length > 0 && !gtmIsSet) {
      useHead({
        script: [
          {
            hid: "gtm",
            children: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
       new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
       j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
       'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
       })(window,document,'script','dataLayer','${form.gtm_id}');`,
            type: "text/javascript"
          }
        ]
      });
      gtmIsSet = true;
    }
  }
});

// const baseUrl = computed(() => { return window.location.host; });

// Mobile detection
const headers = useRequestHeaders();
const userAgent = headers?.["user-agent"] ?? navigator.userAgent;
store.userAgent = userAgent;
store.isMobile =
  userAgent.includes("Mobile") &&
  screen.width <= 992 &&
  window.matchMedia("(orientation: portrait)").matches;
</script>
